import { Link } from "react-router-dom";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Zap, Globe2, Lock, ArrowRight, BadgeCheck, Headphones, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import Calculator from "../components/Calculator";
import Testimonials from "../components/Testimonials";
import { Reveal, RevealGroup, RevealItem } from "../components/Reveal";
import { COMPANY_TAGLINE } from "../lib/site";

const features = [
  { icon: Zap, title: "Instant transfers", desc: "Most NGN↔CAD transfers settle in minutes — not days." },
  { icon: ShieldCheck, title: "Advanced security", desc: "Encrypted by default. Fully KYC and AML compliant on both sides of the corridor." },
  { icon: Globe2, title: "Affordable rates", desc: "Transparent pricing with no hidden fees. What you see in the calculator is what your recipient receives." },
  { icon: Headphones, title: "24/7 support", desc: "Dedicated support, anytime, anywhere — for every transaction." },
  { icon: Smartphone, title: "Built for mobile", desc: "Intuitive design that makes sending money home as easy as sending a text." },
  { icon: Lock, title: "Trust by design", desc: "We obsess over one corridor: NGN ↔ CAD. Better rate, better delivery, better support." },
];

const steps = [
  { n: "01", t: "Sign up & verify", d: "Complete KYC in minutes through our secure identity partner." },
  { n: "02", t: "Get a live quote", d: "See the exact NGN your recipient will receive before you commit." },
  { n: "03", t: "Send & track", d: "Pay from your Canadian bank and track delivery in real time." },
];

const stats = [
  { v: "Live", l: "NGN ↔ CAD rate" },
  { v: "10 min", l: "avg delivery" },
  { v: "24/7", l: "support" },
  { v: "100%", l: "KYC verified" },
];

const easeOut = [0.22, 1, 0.36, 1];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const orbY2 = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const heroFade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO — centered with kinetic parallax layers */}
      <section ref={heroRef} className="relative overflow-hidden">
        {/* Parallax grid background */}
        <motion.div style={{ y: gridY }} className="absolute inset-0 hero-grid-bg" aria-hidden="true" />
        {/* Floating gradient orbs */}
        <motion.div
          style={{ y: orbY }}
          className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#FF6B00]/20 blur-3xl pointer-events-none"
          aria-hidden="true"
        />
        <motion.div
          style={{ y: orbY2 }}
          className="absolute top-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#0B192C]/10 blur-3xl pointer-events-none"
          aria-hidden="true"
        />

        <motion.div
          style={{ opacity: heroFade }}
          className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-12 sm:pb-16 text-center"
        >
          {/* Country flag chips */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: easeOut }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 backdrop-blur border border-slate-200 shadow-sm mb-6"
            data-testid="hero-corridor-chip"
          >
            <span className="text-2xl leading-none">🇨🇦</span>
            <span className="text-xs font-bold uppercase tracking-widest text-slate-700">Canada ↔ Nigeria</span>
            <span className="text-2xl leading-none">🇳🇬</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.08, ease: easeOut }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0B192C] leading-[1.05] max-w-4xl mx-auto"
            data-testid="hero-headline"
          >
            Send money between <span className="text-[#FF6B00]">Canada & Nigeria</span> at the best rate.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.16, ease: easeOut }}
            className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed"
            data-testid="hero-subhead"
          >
            {COMPANY_TAGLINE}. Live rates, a transparent margin, and verified delivery — built for the Nigerian-Canadian community.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.24, ease: easeOut }}
            className="mt-8 flex flex-wrap items-center justify-center gap-3"
          >
            <Link to="/register" data-testid="hero-signup-button">
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Button className="rounded-full bg-[#FF6B00] hover:bg-[#E65C00] text-white h-12 px-7 text-base font-semibold shadow-brand">
                  Sign Up — It&apos;s Free <ArrowRight size={18} className="ml-2" />
                </Button>
              </motion.div>
            </Link>
            <a
              href="https://wa.link/pxadlb"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-bulk-button"
            >
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Button className="rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white h-12 px-7 text-base font-semibold shadow-md inline-flex items-center gap-2">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                    <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.413c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l-.999 3.648 3.978-.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.298.3-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.414z"/>
                  </svg>
                  Bulk Funds Exchange ($5,000 CAD+)
                </Button>
              </motion.div>
            </a>
            <Link to="/about" data-testid="hero-learn-more-button">
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Button variant="outline" className="rounded-full h-12 px-7 border-2 border-slate-200 hover:border-[#0B192C] text-[#0B192C] font-semibold">
                  Learn more
                </Button>
              </motion.div>
            </Link>
          </motion.div>

          {/* KYC verification CTA — sits directly above the calculator */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: easeOut }}
            className="mt-12 sm:mt-14 flex justify-center"
          >
            <a
              href="https://identity.dojah.io/?widget_id=67d17ca0f55304dcbe1c0bfc"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="start-kyc-verification-button"
            >
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                <Button className="rounded-full bg-[#0B192C] hover:bg-[#162846] text-white h-12 px-8 text-base font-semibold shadow-lg inline-flex items-center gap-2 ring-2 ring-[#FF6B00]/40">
                  <ShieldCheck size={18} /> Start KYC Verification
                </Button>
              </motion.div>
            </a>
          </motion.div>

          {/* Calculator centered below */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.32, ease: easeOut }}
            className="mt-5 flex justify-center"
          >
            <Calculator />
          </motion.div>

          {/* Stats strip */}
          <RevealGroup className="mt-12 flex items-center justify-center gap-8 sm:gap-12 text-sm text-slate-500 flex-wrap" data-testid="hero-stats" stagger={0.08}>
            {stats.map((s, i) => (
              <div key={s.l} className="flex items-center gap-8 sm:gap-12">
                <RevealItem className="text-center" y={14}>
                  <div className="font-display font-bold text-2xl text-[#0B192C]">{s.v}</div>
                  <div>{s.l}</div>
                </RevealItem>
                {i < stats.length - 1 && <div className="w-px h-10 bg-slate-200 hidden sm:block" />}
              </div>
            ))}
          </RevealGroup>
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 sm:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-bold uppercase tracking-widest text-[#FF6B00]">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B192C] mt-2">Quick steps to send money home.</h2>
            <p className="mt-4 text-slate-600">Designed to be the simplest part of your week.</p>
          </Reveal>

          <RevealGroup className="mt-14 grid sm:grid-cols-3 gap-5" stagger={0.12}>
            {steps.map((s, i) => (
              <RevealItem key={s.n}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="relative bg-white rounded-3xl p-8 border border-slate-200 hover:border-[#FF6B00]/60 transition-colors h-full"
                  data-testid={`step-${s.n}`}
                >
                  <div className="absolute -top-5 left-8 font-display font-bold text-xl text-white w-12 h-12 rounded-2xl bg-[#FF6B00] flex items-center justify-center shadow-brand">
                    {i + 1}
                  </div>
                  <h3 className="font-display text-xl font-bold text-[#0B192C] mt-6">{s.t}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{s.d}</p>
                </motion.div>
              </RevealItem>
            ))}
          </RevealGroup>

          <Reveal className="text-center mt-12" delay={0.1}>
            <Link to="/register" data-testid="how-it-works-signup">
              <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="inline-block">
                <Button className="rounded-full bg-[#0B192C] hover:bg-[#162846] text-white h-12 px-7 font-semibold">
                  Start now <ArrowRight size={18} className="ml-2" />
                </Button>
              </motion.div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SERVICES / FEATURES */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-bold uppercase tracking-widest text-[#FF6B00]">Our services</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B192C] mt-2">
              <span className="text-[#FF6B00]">Reliable.</span> <span className="text-[#FF6B00]">Fast.</span> <span className="text-[#FF6B00]">Secure.</span> Global transactions with CashPark.
            </h2>
            <p className="mt-4 text-slate-600">The number-one go-to platform for Naira ↔ CAD remittance.</p>
          </Reveal>

          <RevealGroup className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" stagger={0.08}>
            {features.map((f) => (
              <RevealItem key={f.title}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="bg-white border border-slate-200 rounded-3xl p-7 hover:shadow-lg transition-shadow h-full"
                >
                  <div className="w-12 h-12 rounded-2xl bg-orange-50 text-[#FF6B00] flex items-center justify-center">
                    <f.icon size={22} />
                  </div>
                  <h3 className="font-display text-lg font-bold text-[#0B192C] mt-5">{f.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="bg-[#0B192C] rounded-3xl p-10 sm:p-16 text-center relative overflow-hidden">
              <motion.div
                animate={{ scale: [1, 1.15, 1], opacity: [0.2, 0.3, 0.2] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#FF6B00]/20 blur-3xl"
              />
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#FF6B00]/10 blur-3xl"
              />
              <div className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-bold uppercase tracking-widest mb-5">
                <BadgeCheck size={14} /> Ready when you are
              </div>
              <h2 className="relative font-display text-3xl sm:text-5xl font-bold text-white max-w-3xl mx-auto leading-tight">
                Ready for a better Naira–CAD rate?
              </h2>
              <p className="relative mt-4 text-slate-300 max-w-xl mx-auto">Sign up in minutes. Lock today&apos;s rate. Send with confidence.</p>
              <Link to="/register" data-testid="cta-signup-button" className="relative inline-block mt-8">
                <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
                  <Button className="rounded-full bg-[#FF6B00] hover:bg-[#E65C00] text-white h-12 px-8 font-semibold shadow-brand">
                    Get Started <ArrowRight size={18} className="ml-2" />
                  </Button>
                </motion.div>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
